export const CLOCK_FACES = ['digital', 'station', 'classic', 'minimal', 'sport'];
/** Bahnhofsuhr: Sekundenzeiger braucht 58,5 s für die Runde und wartet 1,5 s auf der 12. */
export const STOP_SWEEP_MS = 58_500;
export const FACE_PRESETS = {
    station: { face: '#ffffff', ink: '#111111', accent: '#d8232a', motion: 'stop' },
    classic: { face: '#f6f3ea', ink: '#1d2433', accent: '#b3261e', motion: 'smooth' },
    minimal: { face: '#111111', ink: '#f5f5f5', accent: '#f97316', motion: 'smooth' },
    sport: { face: '#0b0f14', ink: '#f1f5f9', accent: '#2dd4bf', motion: 'tick' },
};
export function isAnalogFace(f) {
    return typeof f === 'string' && f in FACE_PRESETS;
}
/** Gültige IANA-Zeitzone oder undefined (ungültige Eingaben fallen auf die Bildschirm-Zeitzone zurück). */
export function safeTimeZone(tz) {
    if (typeof tz !== 'string' || !tz.trim())
        return undefined;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() });
        return tz.trim();
    }
    catch {
        return undefined;
    }
}
/** Abstand Ortszeit − UTC in Millisekunden (ohne Zeitzone: Systemzeitzone). */
export function tzOffsetMs(at, timeZone) {
    if (!timeZone)
        return -new Date(at).getTimezoneOffset() * 60_000;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(at));
    const p = (t) => Number(parts.find((x) => x.type === t)?.value ?? 0);
    const local = Date.UTC(p('year'), p('month') - 1, p('day'), p('hour') % 24, p('minute'), p('second'));
    return local - Math.floor(at / 1000) * 1000;
}
/** Ortszeit für einen Zeitpunkt; `offset` (aus tzOffsetMs) spart das teure Intl-Formatieren pro Bild. */
export function wallTime(at, timeZone, offset = tzOffsetMs(at, timeZone)) {
    const d = new Date(at + offset);
    return { h: d.getUTCHours(), m: d.getUTCMinutes(), s: d.getUTCSeconds(), ms: d.getUTCMilliseconds() };
}
/** Zeigerwinkel in Grad im Uhrzeigersinn ab 12 Uhr. */
export function handAngles(t, motion) {
    const h12 = t.h % 12;
    if (motion === 'stop') {
        // Sekundenzeiger läuft in 58,5 s herum und bleibt bis zur vollen Minute auf der 12;
        // erst dann springt der Minutenzeiger (der Stundenzeiger folgt in Minutenschritten).
        const inMinute = t.s * 1000 + t.ms;
        return { hour: h12 * 30 + t.m * 0.5, minute: t.m * 6, second: (Math.min(inMinute / STOP_SWEEP_MS, 1) * 360) % 360 };
    }
    const sec = motion === 'smooth' ? t.s + t.ms / 1000 : t.s;
    return { hour: h12 * 30 + t.m * 0.5 + sec / 120, minute: t.m * 6 + sec / 10, second: sec * 6 };
}
// ---------- Farben ----------
/** Nur einfache Farbangaben (Hex, rgb()/hsl(), Farbnamen); alles andere fällt auf die Vorgabe zurück. */
export function safeColor(v, fallback) {
    const s = typeof v === 'string' ? v.trim() : '';
    return /^#[0-9a-f]{3,8}$/i.test(s) || /^(rgb|hsl)a?\([\d\s.,%]+\)$/i.test(s) || /^[a-z]{3,20}$/i.test(s) ? s : fallback;
}
/** Dunkle oder helle Zeigerfarbe passend zur Zifferblattfarbe (nur Hex, sonst undefined). */
export function contrastInk(face) {
    let hex = /^#([0-9a-f]{3,8})$/i.exec(face)?.[1];
    if (!hex)
        return undefined;
    if (hex.length <= 4)
        hex = [...hex].map((c) => c + c).join('');
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5 ? '#111111' : '#f5f5f5';
}
/** Farben einer Uhr: eigene Angaben vor Vorgabe; ohne Zeigerfarbe passt sie sich dem Zifferblatt an. */
export function faceColors(face, o) {
    const p = FACE_PRESETS[face];
    const bg = safeColor(o.faceColor, p.face);
    const ink = o.handColor ? safeColor(o.handColor, p.ink) : o.faceColor ? (contrastInk(bg) ?? p.ink) : p.ink;
    return { face: bg, ink, accent: safeColor(o.accent, p.accent) };
}
// ---------- SVG ----------
const NS = 'http://www.w3.org/2000/svg';
function s(doc, tag, a = {}, children = []) {
    const el = doc.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(a))
        el.setAttribute(k, String(v));
    for (const c of children)
        el.appendChild(c);
    return el;
}
const rot = (deg) => `rotate(${+deg.toFixed(3)})`;
/** Zeichnet das Zifferblatt (viewBox −100…100, Zeiger zeigen auf 12 und werden per rotate() gedreht). */
export function buildFace(doc, face, c, seconds) {
    const svg = s(doc, 'svg', { viewBox: '-100 -100 200 200', class: 'wl-analog-face', 'data-face': face, role: 'img' });
    const add = (el) => svg.appendChild(el);
    const ticks = (fn) => {
        for (let i = 0; i < 60; i++) {
            const el = fn(i);
            if (el)
                add(s(doc, 'g', { transform: rot(i * 6) }, [el]));
        }
    };
    let hour;
    let minute;
    let second = null;
    switch (face) {
        case 'station': {
            add(s(doc, 'circle', { r: 97, fill: c.face, stroke: c.ink, 'stroke-opacity': 0.25, 'stroke-width': 3 }));
            // Stundenbalken (Viertelstunden länger), dazwischen schmale Minutenstriche; keine Ziffern.
            ticks((i) => i % 5
                ? s(doc, 'rect', { x: -1.2, y: -90, width: 2.4, height: 7, fill: c.ink })
                : s(doc, 'rect', { x: -3.7, y: -90, width: 7.4, height: i % 15 ? 21 : 27, fill: c.ink }));
            hour = s(doc, 'rect', { x: -5.5, y: -57, width: 11, height: 75, fill: c.ink });
            minute = s(doc, 'rect', { x: -4, y: -84, width: 8, height: 104, fill: c.ink });
            if (seconds)
                second = s(doc, 'g', {}, [
                    s(doc, 'rect', { x: -1.3, y: -60, width: 2.6, height: 92, fill: c.accent }),
                    s(doc, 'circle', { cy: -60, r: 8.5, fill: c.accent }), // "Kelle"
                ]);
            break;
        }
        case 'classic': {
            add(s(doc, 'circle', { r: 97, fill: c.face, stroke: c.ink, 'stroke-width': 2.5 }));
            add(s(doc, 'circle', { r: 91, fill: 'none', stroke: c.ink, 'stroke-opacity': 0.45, 'stroke-width': 0.7 }));
            ticks((i) => s(doc, 'rect', i % 5 ? { x: -0.45, y: -89, width: 0.9, height: 4, fill: c.ink } : { x: -1.1, y: -89, width: 2.2, height: 8, fill: c.ink }));
            for (let n = 1; n <= 12; n++) {
                const a = (n * 30 * Math.PI) / 180;
                const t = s(doc, 'text', {
                    x: +(Math.sin(a) * 69).toFixed(2), y: +(-Math.cos(a) * 69).toFixed(2), fill: c.ink, 'font-size': 17,
                    'font-family': 'Georgia, "Times New Roman", serif', 'text-anchor': 'middle', 'dominant-baseline': 'central',
                });
                t.textContent = String(n);
                add(t);
            }
            hour = s(doc, 'polygon', { points: '-4,12 4,12 1.6,-46 0,-52 -1.6,-46', fill: c.ink });
            minute = s(doc, 'polygon', { points: '-3,14 3,14 1.1,-74 0,-80 -1.1,-74', fill: c.ink });
            if (seconds)
                second = s(doc, 'rect', { x: -0.6, y: -84, width: 1.2, height: 104, fill: c.accent });
            break;
        }
        case 'minimal': {
            add(s(doc, 'circle', { r: 97, fill: c.face }));
            for (let q = 0; q < 4; q++)
                add(s(doc, 'circle', { cx: [0, 82, 0, -82][q], cy: [-82, 0, 82, 0][q], r: 4.4, fill: c.ink }));
            hour = s(doc, 'line', { y2: -48, stroke: c.ink, 'stroke-width': 7, 'stroke-linecap': 'round' });
            minute = s(doc, 'line', { y2: -76, stroke: c.ink, 'stroke-width': 4.5, 'stroke-linecap': 'round' });
            if (seconds)
                second = s(doc, 'line', { y1: 14, y2: -82, stroke: c.accent, 'stroke-width': 1.8, 'stroke-linecap': 'round' });
            break;
        }
        case 'sport': {
            add(s(doc, 'circle', { r: 97, fill: c.face, stroke: c.ink, 'stroke-opacity': 0.18, 'stroke-width': 2 }));
            add(s(doc, 'circle', { r: 70, fill: 'none', stroke: c.ink, 'stroke-opacity': 0.12, 'stroke-width': 1 }));
            // Minutenstriche, Fünfer kräftig, Viertelstunden in Akzentfarbe.
            ticks((i) => i % 5
                ? s(doc, 'rect', { x: -0.7, y: -91, width: 1.4, height: 6, fill: c.ink, 'fill-opacity': 0.55 })
                : s(doc, 'rect', { x: -2.6, y: -91, width: 5.2, height: 15, rx: 1, fill: i % 15 ? c.ink : c.accent }));
            hour = s(doc, 'rect', { x: -5, y: -50, width: 10, height: 60, rx: 5, fill: c.ink });
            minute = s(doc, 'rect', { x: -3.6, y: -79, width: 7.2, height: 91, rx: 3.6, fill: c.ink });
            if (seconds)
                second = s(doc, 'g', {}, [
                    s(doc, 'rect', { x: -1.6, y: -87, width: 3.2, height: 108, fill: c.accent }),
                    s(doc, 'rect', { x: -3.2, y: 12, width: 6.4, height: 14, rx: 2, fill: c.accent }),
                ]);
            break;
        }
    }
    hour.setAttribute('class', 'hand-h');
    minute.setAttribute('class', 'hand-m');
    second?.setAttribute('class', 'hand-s');
    add(hour);
    add(minute);
    // Achse: farbig unter dem Sekundenzeiger, sonst in Zeigerfarbe.
    const capColor = second ? c.accent : c.ink;
    if (second)
        add(second);
    add(s(doc, 'circle', { r: face === 'sport' ? 6 : face === 'station' ? 3.2 : 3.8, fill: capColor }));
    if (face === 'sport')
        add(s(doc, 'circle', { r: 2, fill: c.face }));
    return { svg, hands: { hour, minute, second } };
}
export function setHands(hands, a) {
    hands.hour.setAttribute('transform', rot(a.hour));
    hands.minute.setAttribute('transform', rot(a.minute));
    hands.second?.setAttribute('transform', rot(a.second));
}
/**
 * Lässt die Zeiger laufen. Gleitende Zeiger mit requestAnimationFrame, sonst ein Timer pro Sekunde
 * (auf die volle Sekunde ausgerichtet). Vorschau: nur sekündlich, keine Animation.
 */
export function runHands(win, o) {
    let offMin = NaN;
    let offset = 0;
    const frame = () => {
        const at = o.now();
        const min = Math.floor(at / 60_000);
        // Zeitzonen-Abstand nur einmal pro Minute berechnen (Sommerzeit wechselt zur vollen Stunde).
        if (min !== offMin) {
            offMin = min;
            offset = tzOffsetMs(at, o.timeZone);
        }
        o.draw(wallTime(at, o.timeZone, offset));
        return at;
    };
    const at = frame();
    const animate = !o.preview && o.seconds && o.motion !== 'tick' && typeof win?.requestAnimationFrame === 'function';
    if (animate) {
        let id = 0;
        const loop = () => {
            frame();
            id = win.requestAnimationFrame(loop);
        };
        id = win.requestAnimationFrame(loop);
        return () => win.cancelAnimationFrame(id);
    }
    let t;
    const next = (last) => {
        t = setTimeout(() => next(frame()), 1000 - (((last % 1000) + 1000) % 1000) + 5);
    };
    next(at);
    return () => clearTimeout(t);
}
/** Auswahl im Designer (Weltuhren); leer = Zeitzone des Bildschirms. */
export const CLOCK_TIME_ZONES = [
    'Europe/Vienna', 'Europe/Berlin', 'Europe/Zurich', 'Europe/London', 'Europe/Lisbon', 'Europe/Istanbul', 'Europe/Moscow',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland', 'UTC',
];
//# sourceMappingURL=analog.js.map